import LFCSelectFormJudge from '_components-with-data/inputs/LFCSelectFormJudge';
import LFCAutocomplete, {
  ChangeParameter,
  multiSelectData
} from '_components/inputs/LFCAutocomplete';
import LFCButton from '_components/inputs/LFCButton';
import LFCDatetimePicker from '_components/inputs/LFCDatetimePicker';
import LFCFormRowGroup from '_components/layout/LFCFormRowGroup';
import ProgressBar from '_components/ProgressBar';
import {getLFCData, handleInputChange2} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {SearchTypeRadioBtn} from '../SearchTypeRadioBtn';
import {SearchConditions} from './types';

interface SearchPanelProps {
  searchConditions: SearchConditions;
  onSearch: (searchConditions: SearchConditions) => void;
}

export const SearchPanel = React.memo(({searchConditions, onSearch}: SearchPanelProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);
  const formRef = useRef<HTMLFormElement>(null!);
  const [conditions, setConditions] = useState<SearchConditions>(searchConditions);

  const [isAutoCompleteReset, setIsAutoCompleteReset] = useState<boolean>(false);
  const [workList, setWorkList] = useState<{label: string}[]>([]);
  const [mapList, setMapList] = useState<{label: string}[]>([]);
  const [deviceidList, setDeviceidList] = useState<{label: string}[]>([]);

  /**
   * 入力値変更
   */
  const handleChange = (event: any) => {
    setConditions(prev => ({...prev, [event.target.name]: event.target.value}));
  };

  /**
   * 入力値変更
   */
  const autoOnChange = (relayDatas: ChangeParameter) => {
    setConditions(prev => ({...prev, [relayDatas.name]: relayDatas.data}));
  };

  /**
   * 検索処理
   */
  const handleSearch = () => {
    if (!formRef.current?.reportValidity()) {
      return;
    }
    onSearch(conditions);
  };

  /**
   * リセット処理
   */
  const handleReset = () => {
    setConditions(searchConditions);
  };

  /**
   * 初期処理
   */
  useEffect(() => {
    setStartProcess(true);

    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      //機種
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 42009,
        parameters: {},
        t,
        cancelToken: cancelSource.current.token
      }).then(ds => {
        const tmp: {label: string}[] = ds.map((item: {work: string}) => {
          return {label: item['work']};
        });
        setWorkList(tmp);
      }),
      //部位
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 30004,
        parameters: {
          select_date_from: conditions.selectDateFrom,
          select_date_to: conditions.selectDateTo,
          work: !isEmpty(conditions.work) ? '{' + conditions.work.join(',') + '}' : null,
          deviceid: !isEmpty(conditions.deviceid)
            ? '{' + conditions.deviceid.join(',') + '}'
            : null,
          judge: conditions.judge === 9 ? null : conditions.judge
        },
        t,
        cancelToken: cancelSource.current.token
      }).then(ds => {
        const tmp: {label: string}[] = ds.map((item: {map: string}) => {
          return {label: item['map']};
        });
        setMapList(tmp);
      }),
      //検査装置
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 42010,
        parameters: {},
        t,
        cancelToken: cancelSource.current.token
      }).then(ds => {
        const tmp: {label: string}[] = ds.map((item: {deviceid: string}) => {
          return {label: item['deviceid']};
        });
        setDeviceidList(tmp);
      })
    ]).finally(() => {
      setStartProcess(false);
    });

    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  return (
    <>
      <ProgressBar startProcess={startProcess} screenLock />
      <form ref={formRef}>
        <LFCFormRowGroup>
          <LFCDatetimePicker
            name={'selectDateFrom'}
            label={t('対象期間From')}
            value={conditions.selectDateFrom}
            required
            onChange={handleChange}
          />
          <LFCDatetimePicker
            name={'selectDateTo'}
            label={t('対象期間To')}
            value={conditions.selectDateTo}
            required
            onChange={handleChange}
          />
          <LFCAutocomplete
            name={'work'}
            label={t('機種')}
            id={'work'}
            value={multiSelectData(workList, conditions.work)}
            onChange={autoOnChange}
            onReset={isAutoCompleteReset}
            doneReset={setIsAutoCompleteReset}
            multiple={true}
            selectItem={workList}
          />
          <LFCAutocomplete
            name={t('map')}
            label={t('検査部位')}
            id={'map'}
            value={multiSelectData(mapList, conditions.map)}
            onChange={autoOnChange}
            onReset={isAutoCompleteReset}
            doneReset={setIsAutoCompleteReset}
            multiple={false}
            selectItem={mapList}
            size="small"
            style={{minWidth: '250px'}}
            required
          />
          <LFCAutocomplete
            name={'deviceid'}
            label={t('検査装置')}
            id={'deviceid'}
            value={multiSelectData(deviceidList, conditions.deviceid)}
            onChange={autoOnChange}
            multiple={true}
            onReset={isAutoCompleteReset}
            doneReset={setIsAutoCompleteReset}
            selectItem={deviceidList}
          />
          <LFCSelectFormJudge
            name={'judge'}
            value={conditions.judge}
            onChange={event => handleInputChange2(event, conditions, setConditions)}
          />
        </LFCFormRowGroup>
        <LFCFormRowGroup>
          <SearchTypeRadioBtn
            value={conditions.searchType}
            onChange={(val: number) => {
              setConditions({...conditions, searchType: val});
            }}
          />

          <LFCButton color="primary" onClick={handleSearch}>
            {t('検索')}
          </LFCButton>
          <LFCButton onClick={handleReset}>{t('リセット')}</LFCButton>
        </LFCFormRowGroup>
      </form>
    </>
  );
});

SearchPanel.displayName = 'SearchPanel';
